import Seo from 'components/seo';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';
import React from 'react';

const CommunityGuidelines = () => {
  return (
    <SiteLayout>
      <Seo title="Community Guidelines | Furbuz Pet Care App" />
      <ContentPage
        title="Community Guidelines"
        description="Last Updated: July 25, 2026"
        eyebrow="Safety"
      >
        <p>
          Furbuz Connect is built for pet parents. These Community Guidelines
          explain what content is welcome, how we review posts and listings, and
          what happens when rules are broken. They apply to Stories, pet service
          listings, Lost &amp; Found alerts, Q&amp;A, comments, and similar
          community content.
        </p>
        <p>
          Binding legal terms still live in our{' '}
          <a href="/terms-and-conditions">Terms and Conditions</a>. This page
          describes how we keep the community safe in practice.
        </p>

        <h2>1. Purpose</h2>
        <p>
          Connect should help people share pet care experiences, find nearby
          services, reunite lost pets, and ask thoughtful questions. Content
          should be pet-related, respectful, and useful to other users.
        </p>

        <h2>2. What is allowed</h2>
        <ul>
          <li>Pet photos, stories, tips, and questions that help other pet parents.</li>
          <li>Accurate pet service listings (vets, groomers, parks, shops, and similar).</li>
          <li>Genuine Lost &amp; Found alerts with clear details.</li>
          <li>Helpful Q&amp;A answers that stay on topic.</li>
          <li>Respectful disagreement and constructive feedback.</li>
        </ul>

        <h2>3. What is not allowed</h2>
        <ul>
          <li>Harassment, hate, threats, bullying, or sexual content.</li>
          <li>Spam, scams, phishing, or deceptive money schemes.</li>
          <li>Off-topic marketplace abuse (for example selling unrelated goods).</li>
          <li>Illegal activity, graphic violence, or content that endangers people or animals.</li>
          <li>Impersonation, false listings, or misleading Lost &amp; Found posts.</li>
          <li>Repeated posting of the same listing to game visibility.</li>
        </ul>

        <h2>4. How review works</h2>
        <p>
          We use automated checks when you submit content. Most clear,
          pet-related posts and listings go live quickly. Some submissions are
          held briefly for a deeper review when signals suggest they need a
          closer look. If your content is held, you will see that it is under
          review and we will notify you when it is approved or not approved.
        </p>
        <p>
          We may also review content after publication when it is reported or
          when safety systems flag it later.
        </p>

        <h2>5. Enforcement and escalation</h2>
        <p>
          If content violates these guidelines, we may reject it, hide it, or
          restrict related features. Account consequences escalate for repeated
          violations within a rolling 30-day window:
        </p>
        <ul>
          <li>
            <strong>First violation:</strong> warning notification and a reminder
            of these guidelines.
          </li>
          <li>
            <strong>Repeated violations (about 3 in 30 days):</strong> temporary
            account suspension of about 7 days.
          </li>
          <li>
            <strong>Further repeated violations (about 5 in 30 days):</strong>{' '}
            longer temporary suspension of about 30 days, with additional admin
            attention.
          </li>
        </ul>
        <p>
          Furbuz does not permanently ban accounts automatically. Permanent bans
          require human review. We may take stronger action sooner for severe
          abuse or illegal activity.
        </p>

        <h2>6. Reporting</h2>
        <p>
          Use the in-app Report option on posts, listings, and profiles when
          something looks unsafe or off-policy. Reporting helps us prioritize
          review. We may hide content, warn users, or suspend accounts when
          reports are substantiated. Reporting tools do not guarantee that every
          issue will be prevented in advance.
        </p>

        <h2>7. Contact</h2>
        <p>
          Questions about these guidelines or an enforcement decision can be sent
          to{' '}
          <a href="mailto:support@furbuz.com">support@furbuz.com</a>.
        </p>

        <h2>8. Changes</h2>
        <p>
          We may update these Community Guidelines as Connect evolves. We will
          post the revised date on this page. Continued use of community features
          means you agree to follow the current guidelines.
        </p>
      </ContentPage>
    </SiteLayout>
  );
};

export default CommunityGuidelines;
